import { useState, type MouseEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { RotateCcw } from 'lucide-react'
import { useOrgsApi } from 'src/api/db/apiOrgs'
import { useQuoteApi } from 'src/api/db/apiQuote'
import { useGetOrgId } from 'src/api/helpers/getOrgId'
import { seedInitialChatNote } from 'src/pages/_myAccount/Requests/Request/components/Chat/hooks'
import { paths } from 'src/routes/paths'
import { SARA_PREFILL_TEXT } from 'src/shared/components/GlobalChatWidget/components/saraPrefill'
import { Button } from 'src/shared/components/ui'
import { cn } from 'src/shared/components/ui/utils'
import { useAuth } from 'src/shared/hooks/authProvider'
import { useToast } from 'src/shared/hooks/use-toast'
import { useLocalization } from 'src/shared/hooks/useLocalization'

export const toSaraReorderMessage = (text: string) => {
  const trimmed = text.trim()
  if (!trimmed) return ''
  if (/^@?sara\b/i.test(trimmed)) return trimmed
  return `${SARA_PREFILL_TEXT}${trimmed}`
}

export const ReorderButton = ({
  message,
  requestName,
  className,
}: {
  message: string
  requestName: string
  className?: string
}) => {
  const navigate = useNavigate()
  const { t } = useLocalization()
  const { toast } = useToast()
  const { session } = useAuth()
  const org_id = useGetOrgId()
  const { organizationById } = useOrgsApi({ org_id })
  const { createQuote } = useQuoteApi({ skipListQuery: true })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isPending = isSubmitting || createQuote.isPending
  const saraMessage = toSaraReorderMessage(message)

  const handleReorder = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    if (!saraMessage || isPending) return

    const orgId = organizationById?.data?.id
    const userId = session?.user?.id
    if (!orgId || !userId) {
      toast({
        variant: 'destructive',
        title: t('Failed', 'Misslyckades'),
        description: t(
          'Unable to create request. Please try again.',
          'Kunde inte skapa förfrågan. Försök igen.'
        ),
      })
      return
    }

    setIsSubmitting(true)
    try {
      const quote = await createQuote.mutateAsync({
        name: requestName,
        org_id: orgId,
      })

      if (!quote?.public_id) {
        throw new Error(
          t(
            'Unable to create request. Please try again.',
            'Kunde inte skapa förfrågan. Försök igen.'
          )
        )
      }

      seedInitialChatNote(quote.public_id, saraMessage)
      navigate(`${paths.myAccount}${paths.requests}/${quote.public_id}`, {
        state: { initialChatMessage: saraMessage },
      })
    } catch (error) {
      console.error('Failed to start reorder:', error)
      toast({
        variant: 'destructive',
        title: t('Failed', 'Misslyckades'),
        description:
          error instanceof Error
            ? error.message
            : t(
                'Unable to create request. Please try again.',
                'Kunde inte skapa förfrågan. Försök igen.'
              ),
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Button
      type="button"
      variant="primary"
      size="default"
      className={cn('w-full shrink-0', className)}
      isLoading={isPending}
      onClick={handleReorder}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <RotateCcw size={16} strokeWidth={2} className="mr-2 shrink-0" />
      {t('Re-order', 'Beställ om')}
    </Button>
  )
}
